// The game store. Wraps the engine reducer in a Zustand store and accumulates
// outbound events into a queue the UI drains for animations.

import { createStore } from 'zustand/vanilla';
import {
  apply,
  initialState,
  type Action,
  type ApplyError,
  type BuilderContent,
  type GameState,
  type InitialStateArgs,
  type OutboundEvent,
} from '@biz-kid/engine';

export type GameStoreState = {
  game: GameState;
  outboundQueue: OutboundEvent[];
};

export type GameStoreActions = {
  /** Dispatch an action. Returns the typed error on failure or null on success. */
  dispatch: (action: Action) => ApplyError | null;
  /** Pop all outbound events accumulated since last drain. */
  drainOutbound: () => OutboundEvent[];
  /** Replace state wholesale (used by persistence restore and time middleware). */
  replaceState: (next: GameState) => void;
};

export type GameStore = GameStoreState & GameStoreActions;

export type CreateGameStoreArgs = {
  content: BuilderContent;
  /** If supplied, the store boots from this state (e.g. restored from snapshot). */
  initial?: GameState;
  /** Otherwise we build a fresh initial state from `initialArgs`. */
  initialArgs?: Omit<InitialStateArgs, 'content'>;
};

export function createGameStore({ content, initial, initialArgs }: CreateGameStoreArgs) {
  const startState =
    initial ??
    initialState({
      content,
      mode: initialArgs?.mode ?? 'builder',
      rngSeed: initialArgs?.rngSeed ?? Date.now() & 0x7fffffff,
      dayKey: initialArgs?.dayKey ?? new Date().toISOString().slice(0, 10),
      ...(initialArgs?.displayName ? { displayName: initialArgs.displayName } : {}),
    });

  return createStore<GameStore>((set, get) => ({
    game: startState,
    outboundQueue: [],

    dispatch: (action) => {
      const r = apply(get().game, action, content);
      if (!r.ok) return r.error;
      set((s) => ({
        game: r.value.state,
        outboundQueue: [...s.outboundQueue, ...r.value.outbound],
      }));
      return null;
    },

    drainOutbound: () => {
      const q = get().outboundQueue;
      if (q.length === 0) return [];
      set({ outboundQueue: [] });
      return q;
    },

    replaceState: (next) => set({ game: next, outboundQueue: [] }),
  }));
}

export type GameStoreInstance = ReturnType<typeof createGameStore>;
